import { clone, isEqual, merge } from 'lodash'
import TodoService, { TODO_FILTER } from '@/modules/todo/todoService'
import isTodoAssignedToMe from '@/modules/todo/components/_helpers/isTodoAssignedToMe'
import getState from '@/modules/todo/components/_helpers/getState'
import TodoStatusEnum from '@/enums/TodoStatus.enum'
import { toast } from '@/modules/base/components/toast/toastConfig'
import lang from '@/lang'

// actions
const ACTION_PREFIX = 'todo'
const SET_ITEMS = `${ACTION_PREFIX}/SET_ITEMS`
const SET_COUNTS = `${ACTION_PREFIX}/SET_COUNTS`
const UPDATE_ITEM = `${ACTION_PREFIX}/UPDATE_ITEM`
const DELETE_ITEM = `${ACTION_PREFIX}/DELETE_ITEM`
const SET_FILTER = `${ACTION_PREFIX}/SET_FILTER`
const SET_ORDER = `${ACTION_PREFIX}/SET_ORDER`
const RESET = `${ACTION_PREFIX}/RESET`
const SET_OFFSET = `${ACTION_PREFIX}/SET_OFFSET`
const SET_ISLOADING = `${ACTION_PREFIX}/SET_ISLOADING`
const ADD_TO_COUNTER = `${ACTION_PREFIX}/ADD_TO_COUNTER`
const SET_ACTIVE_TODO_ID = `${ACTION_PREFIX}/SET_ACTIVE_TODO_ID`

// reducer
const initialState = {
  items: [],
  activeTodoId: null,
  filter: TODO_FILTER?.ALL,
  offset: 0,
  limit: 20,
  counts: {},
  isLoading: false,
  orderBy: null,
}

export default function reducer(state = initialState, action) {
  const { items: previousItems } = state
  switch (action.type) {
    case RESET:
      return { ...state,
        items: [],
        offset: 0,
        limit: 20,
        orderBy: null,
      }
    case SET_ACTIVE_TODO_ID:
      const { id: activeTodoId } = action
      return { ...state, activeTodoId }
    case SET_OFFSET:
      const { offset } = action
      return { ...state,
        items: offset === 0 ? [] : previousItems,
        offset,
      }
    case SET_ORDER:
      const { orderBy } = action
      return { ...state,
        orderBy,
      }
    case SET_ISLOADING:
      const { isLoading } = action
      return { ...state, isLoading }
    case SET_ITEMS:
      const { items } = action
      return { ...state, items }

    case SET_COUNTS:
      const { counts } = action
      return { ...state, counts }

    case SET_FILTER:
      const { filter } = action
      return { ...state, filter }

    case UPDATE_ITEM:
      const { item: newItem } = action
      let newItems = []
      const newCounts = clone(state.counts)
      newCounts[getState(newItem)]++

      const foundItem = state.items.find(item => item.id === newItem.id)
      if (foundItem) {
        newCounts[getState(foundItem)]--
        newItems = state.items.map(item => item.id === newItem.id ? merge(item, clone(newItem)) : item)
      } else {
        newItems = [clone(newItem), ...state.items]
      }

      newCounts[TODO_FILTER.MY] = newItems.reduce((count, item) => {
        return isTodoAssignedToMe(item) && item.status === TodoStatusEnum.DONE ? count + 1 : count
      }, 0)

      return { ...state, items: newItems, counts: newCounts }

    case DELETE_ITEM:
      const { idToRemove } = action
      const foundItemToDelete = state.items.find(item => item.id === idToRemove)
      if (foundItemToDelete) {
        const newCounts = clone(state.counts)
        newCounts[getState(foundItemToDelete)]--
        if (isTodoAssignedToMe(foundItemToDelete)) {
          newCounts[TODO_FILTER.MY]--
        }
        newCounts[TODO_FILTER.ALL] = newCounts[TODO_FILTER.FUTURE] + newCounts[TODO_FILTER.OVERDUE]

        const newItemsClean = state.items.filter(item => idToRemove !== item.id)
        return { ...state, items: newItemsClean, counts: newCounts }
      }
      return state

    case ADD_TO_COUNTER:
      const { id, name, count } = action
      const item = previousItems.find(item => item.id === id)
      if (item && item[name] !== undefined) {
        item[name] = parseInt(item[name] || '0') + parseInt(count)
      }
      return { ...state, items: previousItems }

    default:
      return state
  }
};

// action creators

export const reset = () => {
  return {
    type: RESET,
  }
}

export const setActiveTodoId = (id) => {
  return {
    type: SET_ACTIVE_TODO_ID,
    id,
  }
}

export const setOffset = (offset) => {
  return {
    type: SET_OFFSET,
    offset,
  }
}

export const setOrderBy = (orderBy) => {
  return {
    type: SET_ORDER,
    orderBy,
  }
}

export const setIsLoading = (isLoading) => {
  return {
    type: SET_ISLOADING,
    isLoading,
  }
}

export const setItems = (items) => {
  return {
    type: SET_ITEMS,
    items,
  }
}

export const setCounts = (counts) => {
  return {
    type: SET_COUNTS,
    counts,
  }
}

export const AddToCounter = (id, name, count) => {
  return {
    type: ADD_TO_COUNTER,
    id,
    name,
    count,
  }
}

export const setFilter = (filter) => {
  return {
    type: SET_FILTER,
    filter,
  }
}
export const updateItem = (item) => {
  return {
    type: UPDATE_ITEM,
    item,
  }
}
export const deleteItem = (idToRemove) => {
  return {
    type: DELETE_ITEM,
    idToRemove,
  }
}

export const fetchTodos = options => {
  return (dispatch, getState) => {
    dispatch(setIsLoading(true))
    const { filter, offset, limit, counts, orderBy } = getState().todo.list
    TodoService.getAll({ activeFilter: filter, offset, limit, orderBy, ...options })
      .then((response) => {
        const { data, meta: newTodoCount } = response

        const isCountEqual = isEqual(counts, newTodoCount)
        if (!isCountEqual) {
          dispatch(setCounts(newTodoCount))
        }
        dispatch(setItems(data))
        if (options?.offset) {
          dispatch(setOffset(options.offset))
        }
        if (options?.orderBy) {
          dispatch(setOrderBy(options.orderBy))
        }
        dispatch(setIsLoading(false))
      }).catch(() => {
        toast.showToast({ type: 'error', message: lang.loadingDataErrorMessage })
        dispatch(setIsLoading(false))
      })
  }
}
